import React, { useEffect, useState } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip,
    FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Switch, FormControlLabel,
    Checkbox
} from '@mui/material';
import { LeaveTypeService } from '../../services/leave-type.service';
import { DocumentRequirement, LeaveTypeConfig } from '../../interfaces/leave.interface';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

const LeaveTypeManagement: React.FC = () => {
    const [leaveTypes, setLeaveTypes] = useState<LeaveTypeConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [name, setName] = useState('');
    const [documentRequirement, setDocumentRequirement] = useState<DocumentRequirement>(DocumentRequirement.NONE);
    const [isPaid, setIsPaid] = useState<boolean>(true);
    const toast = useToast();
    const { t } = useTranslation(['leave', 'common']);

    useEffect(() => {
        fetchLeaveTypes();
    }, []);

    const fetchLeaveTypes = async () => {
        try {
            const data = await LeaveTypeService.getByInstitution();
            setLeaveTypes(data);
        } catch (err) {
            toast.showApiError(err, t('leaveTypeManagement.errors.fetch'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!name.trim()) return;
        try {
            await LeaveTypeService.create(name.trim(), documentRequirement, isPaid);
            setDialogOpen(false);
            setName('');
            setDocumentRequirement(DocumentRequirement.NONE);
            setIsPaid(true);
            await fetchLeaveTypes();
            toast.showSuccess(t('leaveTypeManagement.created'));
        } catch (err) {
            toast.showApiError(err, t('leaveTypeManagement.errors.create'));
        }
    };

    const handlePaidStatusChange = async (id: string, checked: boolean) => {
        try {
            await LeaveTypeService.updatePaidStatus(id, checked);
            setLeaveTypes(prev => prev.map(lt =>
                lt.id === id ? { ...lt, isPaid: checked } : lt
            ));
            toast.showSuccess(t('leaveTypeManagement.paidStatusUpdated'));
        } catch (err) {
            toast.showApiError(err, t('leaveTypeManagement.errors.updatePaidStatus'));
        }
    };

    const handleDocRequirementChange = async (id: string, value: DocumentRequirement) => {
        try {
            await LeaveTypeService.updateDocumentRequirement(id, value);
            setLeaveTypes(prev => prev.map(lt =>
                lt.id === id ? { ...lt, documentRequirement: value } : lt
            ));
            toast.showSuccess(t('leaveTypeManagement.documentRequirementUpdated'));
        } catch (err) {
            toast.showApiError(err, t('leaveTypeManagement.errors.updateDocumentRequirement'));
        }
    };

    const handleToggleActive = async (id: string, currentlyActive: boolean) => {
        const nextActive = !currentlyActive;
        const confirmMessage = nextActive
            ? t('leaveTypeManagement.confirmReactivate')
            : t('leaveTypeManagement.confirmDeactivate');
        if (!window.confirm(confirmMessage)) return;
        try {
            await LeaveTypeService.setActiveStatus(id, nextActive);
            setLeaveTypes(prev => prev.map(lt =>
                lt.id === id ? { ...lt, isActive: nextActive } : lt
            ));
            toast.showSuccess(nextActive ? t('leaveTypeManagement.reactivated') : t('leaveTypeManagement.deactivated'));
        } catch (err) {
            toast.showApiError(err, nextActive
                ? t('leaveTypeManagement.errors.reactivate')
                : t('leaveTypeManagement.errors.deactivate'));
        }
    };

    if (loading) return <Typography>{t('loading')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>{t('leaveTypeManagement.title')}</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                <Button variant="contained" onClick={() => { setName(''); setDocumentRequirement(DocumentRequirement.NONE); setIsPaid(true); setDialogOpen(true); }}>
                    {t('leaveTypeManagement.addLeaveType')}
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('leaveTypeManagement.columns.name')}</TableCell>
                            <TableCell>{t('leaveTypeManagement.columns.documentRequirement')}</TableCell>
                            <TableCell>{t('leaveTypeManagement.columns.paid')}</TableCell>
                            <TableCell>{t('leaveTypeManagement.columns.status')}</TableCell>
                            <TableCell>{t('leaveTypeManagement.columns.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leaveTypes.map((lt) => {
                            const active = lt.isActive !== false;
                            return (
                            <TableRow key={lt.id} sx={{ opacity: active ? 1 : 0.55 }}>
                                <TableCell>{lt.name}</TableCell>
                                <TableCell>
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <Select
                                            value={lt.documentRequirement || DocumentRequirement.NONE}
                                            onChange={(e: SelectChangeEvent) =>
                                                handleDocRequirementChange(lt.id, e.target.value as DocumentRequirement)
                                            }
                                        >
                                            {Object.values(DocumentRequirement).map((req) => (
                                                <MenuItem key={req} value={req}>
                                                    {t(`leaveTypeManagement.documentRequirement.${req}`)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        checked={lt.isPaid ?? true}
                                        onChange={(e) => handlePaidStatusChange(lt.id, e.target.checked)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={active ? t('common:status.active') : t('common:status.inactive')}
                                        color={active ? 'success' : 'default'}
                                        size="small"
                                        variant={active ? 'filled' : 'outlined'}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        size="small"
                                        color={active ? 'warning' : 'success'}
                                        onClick={() => handleToggleActive(lt.id, active)}
                                    >
                                        {active ? t('leaveTypeManagement.deactivate') : t('leaveTypeManagement.reactivate')}
                                    </Button>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                        {leaveTypes.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">{t('leaveTypeManagement.empty')}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('leaveTypeManagement.dialogTitle')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label={t('leaveTypeManagement.nameLabel')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="doc-req-label">{t('leaveTypeManagement.documentRequirementLabel')}</InputLabel>
                        <Select
                            labelId="doc-req-label"
                            value={documentRequirement}
                            label={t('leaveTypeManagement.documentRequirementLabel')}
                            onChange={(e: SelectChangeEvent) =>
                                setDocumentRequirement(e.target.value as DocumentRequirement)
                            }
                        >
                            {Object.values(DocumentRequirement).map((req) => (
                                <MenuItem key={req} value={req}>
                                    {t(`leaveTypeManagement.documentRequirement.${req}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isPaid}
                                onChange={(e) => setIsPaid(e.target.checked)}
                            />
                        }
                        label={t('leaveTypeManagement.paidLeave')}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={!name.trim()}>{t('common:actions.create')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeaveTypeManagement;
